import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Search } from 'lucide-react';

/**
 * ⌘K-style command palette that surfaces every major tool, program, and
 * concierge on the FramewUrk. Seeded from the same taxonomy as /tools so
 * adding new items there automatically flows here. Opens via:
 *   - the magnifying-glass button in the header (mouse)
 *   - ⌘K / Ctrl+K anywhere on the site (keyboard)
 */
const COMMANDS = [
  {
    group: 'Find Capital',
    items: [
      { label: 'Grant Concierge AI', to: '/grant-concierge', keywords: 'grants ai match funding' },
      { label: 'Nonprofit Grants Pipeline', to: '/nonprofit-grants', keywords: 'grants pipeline nonprofit' },
      { label: 'Capital & Lending Marketplace', to: '/capital-marketplace', keywords: 'capital loans lenders' },
      { label: 'Louisiana Lender Match', to: '/lender-match', keywords: 'sba lender louisiana' },
      { label: 'Capital Readiness Hub', to: '/capital-readiness', keywords: 'readiness fundable' },
      { label: 'Federal Contracting', to: '/federal-contracting', keywords: 'sam cage bid federal contract' },
      { label: 'DeFi Lending', to: '/defi-lending', keywords: 'defi crypto lending' },
      { label: 'Financial Literacy', to: '/financial-literacy', keywords: 'money finance coaching' },
      { label: 'Compliance Concierge', to: '/compliance-concierge', keywords: 'compliance concierge filings stay right' },
    ],
  },
  {
    group: 'For Entrepreneurs',
    items: [
      { label: 'Business Directory', to: '/businesses', keywords: 'business directory list' },
      { label: 'Interactive Business Map', to: '/business-map', keywords: 'map businesses louisiana' },
      { label: 'Service Provider Marketplace', to: '/service-providers', keywords: 'services hire vendors' },
      { label: 'Professional Services', to: '/professional-services', keywords: 'professionals consultants' },
      { label: '10-Step Launch Roadmap', to: '/launch-roadmap', keywords: 'launch startup roadmap' },
      { label: 'Startup Cost Calculator', to: '/launch-calculator', keywords: 'startup costs calculator burn runway' },
      { label: 'Business Structure Chooser', to: '/business-structure', keywords: 'llc scorp entity business structure' },
      { label: 'Business Plan Builder (AI)', to: '/business-plan', keywords: 'business plan ai' },
      { label: 'Business Formation Wizard', to: '/business-formation', keywords: 'formation llc ein' },
      { label: 'Permit Lookup', to: '/permit-lookup', keywords: 'permits parish lookup' },
      { label: 'AI Pitch Practice', to: '/pitch-practice', keywords: 'pitch practice ai' },
      { label: 'SOP Builder', to: '/sop-builder', keywords: 'sop standard operating procedure' },
      { label: 'wUrkForce Development', to: '/workforce', keywords: 'workforce jobs career' },
      { label: 'Retail Launchpad', to: '/retail-launchpad', keywords: 'retail brick mortar store' },
    ],
  },
  {
    group: 'For Nonprofits',
    items: [
      { label: 'PROOF for Cohorts', to: '/nonprofits', keywords: 'proof cohorts credentials' },
      { label: 'PROOF Pricing & Plans', to: '/pricing', keywords: 'pricing tiers starter growth anchor' },
      { label: 'Apply for PROOF Free', to: '/nonprofits/signup', keywords: 'free proof signup apply' },
      { label: 'Canonical Org Profile', to: '/profile', keywords: 'profile ein uei cage' },
      { label: 'How to Start a Nonprofit (LA)', to: '/nonprofits/start-a-nonprofit', keywords: 'start nonprofit louisiana' },
      { label: 'Federal Funding Advocacy', to: '/nonprofits/advocacy', keywords: 'federal funding advocacy safe language' },
      { label: 'PROOF Directory', to: '/nonprofits/directory', keywords: 'nonprofits directory' },
      { label: 'PROOF Verified Embed Kit', to: '/nonprofits/embed-kit', keywords: 'embed widget verified' },
      { label: 'Community Needs Assessment', to: '/needs-assessment', keywords: 'needs assessment survey' },
      { label: 'Nonprofit Ops Suite', to: '/nonprofit-ops', keywords: 'nonprofit ops board' },
    ],
  },
  {
    group: 'Prove Impact',
    items: [
      { label: 'PROOF Network Impact Dashboard', to: '/dowurk/impact', keywords: 'impact dashboard network metrics' },
      { label: 'PROOF Overview', to: '/proof', keywords: 'proof overview credentials' },
      { label: 'PROOF Passport', to: '/proof/passport', keywords: 'passport credentials wallet' },
      { label: 'PROOF Scoreboard', to: '/proof/scoreboard', keywords: 'scoreboard leaderboard' },
      { label: 'Open Badges', to: '/badges', keywords: 'open badges ims credentials' },
      { label: '30-Day Resilience Pathway', to: '/proof/resilience-pathway', keywords: 'resilience pathway 30 day' },
      { label: 'Resilience Wall', to: '/proof/resilience-wall', keywords: 'resilience wall public' },
    ],
  },
  {
    group: 'Community & Wellbeing',
    items: [
      { label: 'Youth Track (13–18)', to: '/youth-track', keywords: 'youth teens entrepreneurship' },
      { label: 'Community Track', to: '/community-track', keywords: 'community organizers advocates' },
      { label: "Seven F's FramewUrk", to: '/framework', keywords: 'seven fs framework faith fitness food' },
      { label: 'Faith wUrks', to: '/faith', keywords: 'faith religion ministry' },
      { label: 'wUrkFit', to: '/fitness', keywords: 'fitness wellness exercise' },
      { label: 'School 4 Thought', to: '/foundation', keywords: 'school 4 thought foundation education' },
      { label: 'Farm2Table', to: '/food', keywords: 'farm food sovereignty' },
      { label: 'LegacyStudio', to: '/legacystudio', keywords: 'legacy podcast story studio' },
      { label: 'Mental Health Center', to: '/mental-health', keywords: 'mental health wealth crisis wellness burnout resilience 7fs audit' },
      { label: 'Disaster Hub', to: '/disaster-hub', keywords: 'disaster storm recovery' },
      { label: 'Veterans Support', to: '/veterans', keywords: 'veterans support military' },
      { label: 'Community Discussion', to: '/forum', keywords: 'forum discussion' },
      { label: 'Mentor Match', to: '/mentors', keywords: 'mentor match' },
      { label: 'Events', to: '/events', keywords: 'events workshops mixers' },
      { label: 'Volunteer Hub', to: '/volunteer-hub', keywords: 'volunteer hub' },
      { label: 'Gratitude Wall', to: '/gratitude-wall', keywords: 'gratitude thanks' },
    ],
  },
  {
    group: 'AI & Learning',
    items: [
      { label: 'AI Assistant', to: '/ai-assistant', keywords: 'ai assistant chat' },
      { label: 'AI Fundamentals', to: '/ai-fundamentals', keywords: 'ai fundamentals intro' },
      { label: 'AI Training Curriculum', to: '/ai-curriculum', keywords: 'ai training curriculum courses' },
      { label: 'AI Generalist Program', to: '/ai-generalist', keywords: 'ai generalist program paid' },
      { label: 'AI Tools Directory', to: '/ai-generalist/tools', keywords: 'ai tools directory 500' },
      { label: 'My Learning', to: '/lms', keywords: 'learning lms courses' },
      { label: 'Resource Library', to: '/resources', keywords: 'resources library templates' },
    ],
  },
  {
    group: 'Support & Account',
    items: [
      { label: 'Give / Donate', to: '/donate', keywords: 'give donate support' },
      { label: 'Fund a Dream Project', to: '/fundraiser', keywords: 'dream project fund' },
      { label: 'Shop Merch', to: '/shop', keywords: 'shop merch store' },
      { label: 'All Tools Directory', to: '/tools', keywords: 'all tools index directory' },
      { label: 'Dashboard', to: '/dashboard', keywords: 'dashboard my account' },
      { label: 'Premium Membership', to: '/premium', keywords: 'premium membership' },
      { label: 'Contact Us', to: '/contact', keywords: 'contact email support' },
    ],
  },
];

export default function QuickSearch() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Keyboard shortcut: ⌘K on Mac, Ctrl+K elsewhere.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const jump = useCallback((to) => {
    setOpen(false);
    navigate(to);
  }, [navigate]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Quick search (Cmd+K)"
        data-testid="header-quick-search-btn"
        className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-500 hover:border-emerald-400 hover:bg-emerald-50 hover:text-emerald-800 transition-colors shrink-0"
      >
        <Search className="h-3.5 w-3.5" />
        <span className="hidden xl:inline">Search tools…</span>
        <kbd className="ml-1 hidden xl:inline-flex items-center rounded border border-slate-300 bg-slate-100 px-1 py-0.5 text-[9px] font-mono text-slate-600">
          ⌘K
        </kbd>
      </button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Search tools, grants, programs, concierges…" data-testid="quick-search-input" />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          {COMMANDS.map((group, idx) => (
            <div key={group.group}>
              {idx > 0 && <CommandSeparator />}
              <CommandGroup heading={group.group}>
                {group.items.map((item) => (
                  <CommandItem
                    key={item.to}
                    value={`${item.label} ${item.keywords}`}
                    onSelect={() => jump(item.to)}
                    data-testid={`quick-search-item-${item.to.replace(/\//g, '-').replace(/^-/, '')}`}
                  >
                    {item.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </div>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
}
